import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import { AspectRatio } from '@mantine/core';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';

//////////////////////////////////////////////////////
// EmbedComponent
//////////////////////////////////////////////////////

type EmbedComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  src: string;
}>;

function EmbedComponent({ className, format, nodeKey, src }: EmbedComponentProps) {
  return (
    <BlockWithAlignableContents className={className} format={format} nodeKey={nodeKey}>
      <AspectRatio ratio={16 / 9}>
        <iframe
          src={src}
          sandbox="allow-scripts allow-popups allow-top-navigation-by-user-activation allow-forms allow-same-origin allow-storage-access-by-user-activation allow-popups-to-escape-sandbox"
          allowFullScreen
          title="Embed"
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '1px',
            pointerEvents: 'auto',
            backgroundColor: 'rgb(25, 25, 25)',
          }}
        />
      </AspectRatio>
    </BlockWithAlignableContents>
  );
}

//////////////////////////////////////////////////////
// Serialized Iframe Embed Node Type
//////////////////////////////////////////////////////

export type SerializedEmbedNode = Spread<
  {
    src: string;
  },
  SerializedDecoratorBlockNode
>;

//////////////////////////////////////////////////////
// DOM Conversion Helper
//////////////////////////////////////////////////////

function $convertIframeElement(domNode: HTMLElement): null | DOMConversionOutput {
  const src = domNode.getAttribute('data-lexical-iframe');
  if (src) {
    const node = $createEmbedNode(src);
    return { node };
  }
  return null;
}

//////////////////////////////////////////////////////
// EmbedNode Class
//////////////////////////////////////////////////////

export class EmbedNode extends DecoratorBlockNode {
  __src: string;

  static getType(): string {
    return 'embed';
  }

  static clone(node: EmbedNode): EmbedNode {
    return new EmbedNode(node.__src, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedEmbedNode): EmbedNode {
    const node = $createEmbedNode(serializedNode.src);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedEmbedNode {
    return {
      ...super.exportJSON(),
      type: 'embed',
      version: 1,
      src: this.__src,
    };
  }

  constructor(src: string, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__src = src;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('iframe');
    element.setAttribute('data-lexical-iframe', this.__src);
    element.setAttribute('width', '560');
    element.setAttribute('height', '315');
    element.setAttribute('src', this.__src);
    element.setAttribute('frameborder', '0');
    element.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    element.setAttribute('allowfullscreen', 'true');
    element.setAttribute('title', 'Embed');
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      iframe: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-iframe')) {
          return null;
        }
        return {
          conversion: $convertIframeElement,
          priority: 1,
        };
      },
    };
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getTextContent(_includeInert?: boolean, _includeDirectionless?: boolean): string {
    return this.__src;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || '',
      focus: embedBlockTheme.focus || '',
    };
    return (
      <EmbedComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        src={this.__src}
      />
    );
  }
}

//////////////////////////////////////////////////////
// Helper Functions
//////////////////////////////////////////////////////

export function $createEmbedNode(src: string): EmbedNode {
  return new EmbedNode(src);
}

export function $isEmbedNode(node: EmbedNode | LexicalNode | null | undefined): node is EmbedNode {
  return node instanceof EmbedNode;
}
